import type { ReactNode } from 'react';
import React, { useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { Bleed, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

const styles = cssMap({
	psaBleed: {
		width: '100%',
	},
	bleed: {
		width: '100%',
		paddingInlineEnd: token('space.050'),
		height: '56px',
		paddingBlock: token('space.025'),
	},
	pressable: {
		padding: token('space.0'),
		width: '100%',
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

const i18n = defineMessages({
	expand: {
		id: 'share-and-restrict-dialog.rowInteractionWrapper.expand',
		defaultMessage: 'View specific access',
		description: 'Aria label when row is collapsed',
	},
	collapse: {
		id: 'share-and-restrict-dialog.rowInteractionWrapper.collapse',
		defaultMessage: 'Collapse',
		description: 'Aria label when row is expanded',
	},
});

export type RowInteractionWrapperProps = {
	/**
	 * The content to render inside the pressable row
	 * Can be ReactNode or a function that receives hover state
	 */
	children: ReactNode | ((props: { rowHovered: boolean }) => ReactNode);

	/**
	 * Whether the row/content is expanded
	 */
	isExpanded: boolean;

	/**
	 * Callback fired when expand/collapse is triggered (non-experiment mode)
	 */
	onExpandClick: () => void;

	/**
	 * Callback fired when opening the specific access dialog (experiment mode)
	 */
	onOpenSpecificAccessDialog: () => void;

	/**
	 * Optional aria label override
	 */
	ariaLabel?: string;

	isSpecificAccessDialogOpen: boolean;
};

/**
 * A reusable wrapper component that handles the interaction patterns for rows
 * in the Share and Restrict Dialog. It provides:
 */
export const RowInteractionWrapper = ({
	children,
	isExpanded,
	onExpandClick,
	onOpenSpecificAccessDialog,
	ariaLabel,
	isSpecificAccessDialogOpen = false,
}: RowInteractionWrapperProps): ReactNode => {
	const [rowHovered, setRowHovered] = useState(false);
	const { formatMessage } = useIntl();

	const handleClick = useCallback(() => {
		if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
			onOpenSpecificAccessDialog();
		} else {
			onExpandClick();
		}
	}, [onOpenSpecificAccessDialog, onExpandClick]);

	const finalAriaLabel =
		ariaLabel || (isExpanded ? formatMessage(i18n.collapse) : formatMessage(i18n.expand));

	if (isSpecificAccessDialogOpen) {
		return typeof children === 'function' ? children({ rowHovered }) : children;
	}

	return (
		<Bleed
			inline="space.100"
			xcss={cx(
				styles.psaBleed,
				!expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') && styles.bleed,
			)}
		>
			<Pressable
				onClick={handleClick}
				onMouseEnter={() => setRowHovered(true)}
				onMouseLeave={() => setRowHovered(false)}
				xcss={styles.pressable}
				aria-label={finalAriaLabel}
			>
				{typeof children === 'function' ? children({ rowHovered }) : children}
			</Pressable>
		</Bleed>
	);
};
